import React from 'react';
import type { ReactNode } from 'react';
import type { PrimitiveType } from 'intl-messageformat';
import { useIntl } from 'react-intl';
import { TExperimentVariant } from '@vNext/types/Experiments';

type PlainValues = Record<string, PrimitiveType>;
type RichValues = Parameters<ReturnType<typeof useIntl>['formatMessage']>[1];

type ExperimentVariant = TExperimentVariant | 'default';

const defaultRichTextElements = {
    b: (chunks: ReactNode) => <strong>{ chunks }</strong>,
    strong: (chunks: ReactNode) => <strong>{ chunks }</strong>,
    i: (chunks: ReactNode) => <em>{ chunks }</em>,
    em: (chunks: ReactNode) => <em>{ chunks }</em>,
    u: (chunks: ReactNode) => <u>{ chunks }</u>,
    br: () => <br />,
};

export const useTranslation = () => {
    const intl = useIntl();

    const resolveKey = (key: string, experimentVariant?: ExperimentVariant): string => {
        if (!experimentVariant) return key;

        const messages = intl.messages as Record<string, string>;
        const variantKey = `${key}.${experimentVariant}`;
        const defaultKey = `${key}.default`;

        return variantKey in messages ? variantKey : defaultKey;
    };

    const t = (
        key: string,
        values?: PlainValues,
        experimentVariant?: ExperimentVariant
    ): string =>
        intl.formatMessage({ id: resolveKey(key, experimentVariant) }, values);

    const rt = (
        key: string,
        values?: RichValues,
        experimentVariant?: ExperimentVariant
    ): ReactNode =>
        intl.formatMessage({ id: resolveKey(key, experimentVariant) }, { ...defaultRichTextElements, ...values });

    return { t, rt };
};

export type TFunction = ReturnType<typeof useTranslation>['t'];
export type RTFunction = ReturnType<typeof useTranslation>['rt'];
